// wsnet.ts — transport WebSocket dla silnika Xash3D (zamiast upstreamowego WebRTC).
// Ramki binarne WS przechodzą 1:1 jako datagramy GoldSrc; gateway (server.js)
// mostkuje je do UDP silnika. Silnik "łączy się" z 127.0.0.1:8080 — to adres
// wirtualny: liczy się tylko to, że pakiety przychodzące mają ten sam adres.
import { Net, Packet, Xash3D, Xash3DOptions } from 'xash3d-fwgs';

export const FAKE_SERVER_IP: [number, number, number, number] = [127, 0, 0, 1];
export const FAKE_SERVER_PORT = 8080;

// Kanał sterujący (tekstowe ramki WS) — sygnalizacja czatu głosowego (GAME.md §8).
// Binarne ramki = pakiety silnika 1:1; tekstowe = warstwa aplikacji (voice). Gra
// niżej (silnik WASM) tekstu nigdy nie widzi.
export type VoiceHandlers = {
  hello?: (selfId: number, peers: number[]) => void;
  join?: (id: number) => void;
  leave?: (id: number) => void;
  signal?: (from: number, payload: unknown) => void;
  reset?: () => void; // WS padł: zwolnij mikrofon / zamknij połączenia
  chat?: (from: string, text: string) => void;
};

export class Xash3DWS extends Xash3D {
  private ws?: WebSocket;
  private url?: string;
  private closedByUs = false;
  onNetDown?: () => void;

  // ── voice bus: glue (public/js/voice.js) rejestruje handlery przez onVoice ──
  private vh: VoiceHandlers = {};
  private lastHello: { selfId: number; peers: number[] } | null = null;

  constructor(opts?: Xash3DOptions) {
    super(opts);
    this.net = new Net(this);
  }

  // rejestracja handlerów głosu; powitanie (voice-hello) bufforujemy, bo gateway
  // wysyła je zaraz po otwarciu WS — możliwe PRZED podpięciem glue.
  onVoice(h: VoiceHandlers) {
    this.vh = { ...this.vh, ...h };
    if (this.lastHello && this.vh.hello) this.vh.hello(this.lastHello.selfId, this.lastHello.peers);
  }

  // wyślij sygnalizację WebRTC do JEDNEGO gracza (serwer przekaże po id)
  sendVoiceSignal(to: number, payload: unknown) {
    this.sendControl({ t: 'voice-signal', to, payload });
  }

  // wyślij wiadomość czatu tekstowego (broadcast, serwer rozgłasza dalej)
  sendChat(text: string, nick: string) {
    this.sendControl({ t: 'chat', text, nick });
  }

  private sendControl(obj: unknown) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try { this.ws.send(JSON.stringify(obj)); } catch { /* noop */ }
    }
  }

  private handleControl(text: string) {
    let m: any;
    try { m = JSON.parse(text); } catch { return; }
    if (!m || typeof m.t !== 'string') return;
    switch (m.t) {
      case 'voice-hello':
        this.lastHello = { selfId: m.selfId, peers: Array.isArray(m.peers) ? m.peers : [] };
        this.vh.hello?.(this.lastHello.selfId, this.lastHello.peers);
        break;
      case 'voice-join': this.vh.join?.(m.id); break;
      case 'voice-leave': this.vh.leave?.(m.id); break;
      case 'voice-signal': this.vh.signal?.(m.from, m.payload); break;
      case 'chat': this.vh.chat?.(m.from, m.text); break;
      default: break;
    }
  }

  connectWS(url: string, timeoutMs = 15000): Promise<void> {
    this.url = url;
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      ws.binaryType = 'arraybuffer';
      const timer = setTimeout(() => {
        try { ws.close(); } catch { /* noop */ }
        reject(new Error('WS connect timeout'));
      }, timeoutMs);
      ws.onopen = () => {
        clearTimeout(timer);
        this.ws = ws;
        resolve();
      };
      ws.onerror = () => {
        clearTimeout(timer);
        reject(new Error('WS error'));
      };
      ws.onmessage = (ev: MessageEvent) => {
        if (typeof ev.data === 'string') { this.handleControl(ev.data); return; } // kanał sterujący (voice)
        if (!(ev.data instanceof ArrayBuffer)) return;
        const packet: Packet = {
          ip: FAKE_SERVER_IP,
          port: FAKE_SERVER_PORT,
          data: new Int8Array(ev.data),
        };
        (this.net as Net).incoming.enqueue(packet);
      };
      ws.onclose = () => {
        this.ws = undefined;
        this.lastHello = null;
        try { this.vh.reset?.(); } catch { /* voice nie może psuć teardownu */ }
        if (!this.closedByUs) this.onNetDown?.();
      };
    });
  }

  closeWS() {
    this.closedByUs = true;
    try { this.ws?.close(); } catch { /* noop */ }
    this.ws = undefined;
  }

  // wywoływane przez Net przy każdym wychodzącym pakiecie silnika
  sendto(packet: Packet) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    if (this.ws.bufferedAmount > 1 << 20) return; // drop przy zatorze (jak UDP)
    this.ws.send(packet.data);
  }
}
